import {
  ContractorType,
  LegalEntityList,
  PersonList,
  VehicleList,
  VehicleType
} from '@insurance/api/requests'
import {
  AddPersonButton,
  AddVehicleButton,
  RadioCardGroup
} from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { Col, Flex, Form, FormItemProps, Input, Row, Select } from 'antd'
import { Add } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

type Props = {
  certificateValidator: Required<FormItemProps>['rules'][number]
}

export const InsuranceRcaMainStep = ({ certificateValidator }: Props) => {
  const { t } = useTranslation()
  const { required, personalCode } = useValidationsField()
  const formInstance = Form.useFormInstance()

  const vehicleTypeProps = { name: 'vehicle_type' }
  const contractorTypeProps = { name: 'contractor_type' }
  const identificationNumberProps = {
    name: 'identification_number',
    label: `${t('identification_number')} / ${t('fiscal_code')}`
  }
  const certificateNumberProps = {
    name: 'certificate_number',
    label: t('certificate_number')
  }
  const trailerCertificateNumberProps = {
    name: 'trailer_certificate_number',
    label: t('trailer_registration_certificate')
  }
  const contractorPersonProps = { name: 'contractor_person' }
  const vehicleSelectedProps = { name: 'vehicle_selected' }
  const trailerVehicleSelectedProps = { name: 'trailer_vehicle_selected' }

  const vehicleType = Form.useWatch(vehicleTypeProps.name, { preserve: true })
  const contractorType = Form.useWatch<ContractorType>(
    contractorTypeProps.name,
    { preserve: true }
  )
  const contractorPerson = Form.useWatch(contractorPersonProps.name, {
    preserve: true
  })
  const vehicleSelected = Form.useWatch<VehicleList>(
    vehicleSelectedProps.name,
    { preserve: true }
  )
  const trailerVehicleSelected = Form.useWatch<VehicleList>(
    trailerVehicleSelectedProps.name,
    { preserve: true }
  )

  const isTrailer = vehicleType === VehicleType.TRAILER

  const vehicleTypeOptions = [
    { label: t('vehicle'), value: VehicleType.VEHICLE },
    { label: t('trailer_rca'), value: VehicleType.TRAILER }
  ]

  const handleSelectContractor = (person?: PersonList | LegalEntityList) => {
    formInstance.setFieldsValue({
      [identificationNumberProps.name]: person?.identification_number ?? ''
    })
  }

  const handleSelectVehicle = (vehicle?: VehicleList) => {
    formInstance.setFieldsValue({
      [certificateNumberProps.name]: vehicle?.certificate_number ?? ''
    })
  }

  const handleSelectTrailerVehicle = (vehicle?: VehicleList) => {
    formInstance.setFieldsValue({
      [trailerCertificateNumberProps.name]: vehicle?.certificate_number ?? ''
    })
  }

  return (
    <Row gutter={[12, 12]}>
      {/* Contractor type */}
      <Col xs={24}>
        <Form.Item {...contractorTypeProps} rules={[required]}>
          <RadioCardGroup
            options={[
              { value: ContractorType.PERSON, label: t('person') },
              { value: ContractorType.LEGALENTITY, label: t('legal_entity') }
            ]}
          />
        </Form.Item>
      </Col>

      {/* Identification number */}
      <Col xs={24}>
        <Form.Item label={identificationNumberProps.label}>
          <Flex gap={8} align="center">
            <Form.Item
              name={identificationNumberProps.name}
              rules={[required, personalCode]}
              noStyle
            >
              <Input placeholder={identificationNumberProps.label} />
            </Form.Item>

            <AddPersonButton
              personType={contractorType || ContractorType.PERSON}
              type="primary"
              icon={<Add size={16} />}
              value={contractorPerson}
              multiple={false}
              onAdd={handleSelectContractor}
            >
              {t('select')}
            </AddPersonButton>
          </Flex>
        </Form.Item>
      </Col>

      {/* Vehicle type */}
      <Col xs={24}>
        <Form.Item {...vehicleTypeProps} label={t('vehicle_type')}>
          <Select
            options={vehicleTypeOptions}
            placeholder={t('vehicle_type')}
          />
        </Form.Item>
      </Col>

      {/* Trailer certificate */}
      {isTrailer && (
        <Col xs={24}>
          <Form.Item label={trailerCertificateNumberProps.label}>
            <Flex gap={8} align="center">
              <Form.Item
                name={trailerCertificateNumberProps.name}
                rules={[required, certificateValidator]}
                noStyle
              >
                <Input placeholder={trailerCertificateNumberProps.label} />
              </Form.Item>

              <AddVehicleButton
                vehicleType={VehicleType.TRAILER}
                multiple={false}
                type="primary"
                icon={<Add size={16} />}
                value={trailerVehicleSelected}
                onAdd={handleSelectTrailerVehicle}
              >
                {t('select')}
              </AddVehicleButton>
            </Flex>
          </Form.Item>
        </Col>
      )}

      {/* Vehicle certificate */}
      <Col xs={24}>
        <Form.Item
          label={
            isTrailer
              ? t('towing_vehicle_registration_certificate')
              : t('vehicle_registration_certificate')
          }
        >
          <Flex gap={8} align="center">
            <Form.Item
              name={certificateNumberProps.name}
              rules={[required, certificateValidator]}
              noStyle
            >
              <Input
                placeholder={
                  isTrailer
                    ? t('towing_vehicle_registration_certificate')
                    : t('vehicle_registration_certificate')
                }
              />
            </Form.Item>

            <AddVehicleButton
              vehicleType={VehicleType.VEHICLE}
              multiple={false}
              type="primary"
              icon={<Add size={16} />}
              value={vehicleSelected}
              onAdd={handleSelectVehicle}
            >
              {t('select')}
            </AddVehicleButton>
          </Flex>
        </Form.Item>
      </Col>
    </Row>
  )
}
